import { useQuery } from "@apollo/client/react";
import { Box, Flex, IconicButton, Sidebar } from "@nulogy/components";
import ApolloLoadingAnimation from "components/common/ApolloLoadingAnimation";
import DeleteConfirmationModal from "components/common/DeleteConfirmationModal";
import { getExpandedFieldCount } from "components/pages/schedule/helpers/workOrderCardHelper";
import { expandedSidebarWidth } from "components/pages/schedule/sidebarSizes";
import WorkOrderSearch from "components/pages/schedule/WorkOrderSearch";
import { GetLayoutSetting } from "hooks/apollo/customerSettings/customerSettings.gql";
import useDeleteWorkOrder from "hooks/apollo/workOrder/useDeleteWorkOrder";
import useDeleteConfirmationModal from "hooks/state/useDeleteConfirmationModal";
import React from "react";
import styled from "styled-components";
import ReactExt from "utils/ReactExt";
import SidebarExpander from "./SidebarExpander";
import VirtualizedWorkOrderList from "./VirtualizedWorkOrderList";

const EditFiltersButton = styled(IconicButton)`
  vertical-align: bottom;
  padding: 0;
`;

const NarrowSidebar = styled(Sidebar)(({ theme }) => ({
  ".ReactVirtualized__Grid.ReactVirtualized__List": {
    paddingLeft: theme.sizes.x1,
    paddingRight: theme.sizes.x1,
  },
  "& > div": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& > div:first-child": {
    paddingLeft: theme.sizes.x1,
    paddingRight: theme.sizes.x1,
  },
}));

function SidebarView({
  workOrders,
  onTextSearch,
  onCategoryChange,
  numberOfFiltersApplied,
  slideOutState,
  onToggleSidebar,
  sidebarOpen,
  searchText,
  searchCategory,
  fetching,
  searchCategoryOptions,
}) {
  const [expandedWorkOrders, setExpandedWorkOrders] = React.useState([]);
  const { data: layoutSettingData, loading: layoutSettingLoading } = useQuery(GetLayoutSetting);
  const ListRef = React.createRef();
  const { data: deleteData } = useDeleteConfirmationModal();
  const removeWorkOrder = useDeleteWorkOrder();

  if (layoutSettingLoading) return null;

  const refreshList = () => {
    if (ListRef.current) {
      ListRef.current.recomputeRowHeights();
      ListRef.current.forceUpdate();
    }
  };

  const onToggleExpandWorkOrder = (workOrderId) => {
    if (expandedWorkOrders.includes(workOrderId)) {
      setExpandedWorkOrders(expandedWorkOrders.filter((id) => id !== workOrderId));
    } else {
      setExpandedWorkOrders([...expandedWorkOrders, workOrderId]);
    }

    refreshList();
  };

  const onExpandAll = () => {
    setExpandedWorkOrders(workOrders.map((workOrder) => workOrder.id));
    refreshList();
  };

  const onCollapseAll = () => {
    setExpandedWorkOrders([]);
    refreshList();
  };

  const handleClickEditFilters = () => {
    if (slideOutState.isFilterMode()) {
      slideOutState.closeSlideOut();
    } else {
      slideOutState.openFilterSidebar();
    }
  };

  const handleRemoveWorkOrder = () => {
    const areDetailsOpen = deleteData.id === slideOutState.workOrderId;
    if (areDetailsOpen) {
      slideOutState.closeSlideOut();
    }

    removeWorkOrder({ workOrderId: deleteData.id });
  };

  const filterLabel = `Filters ${numberOfFiltersApplied > 0 ? `(${numberOfFiltersApplied})` : ""}`;

  const expandedFieldCount = getExpandedFieldCount(layoutSettingData?.workOrderLayoutSetting || []);

  return (
    <>
      <DeleteConfirmationModal
        message={`This action will delete the work order (${deleteData?.code}) and all its scheduled blocks. However, it will not delete its published jobs, if any.`}
        onDelete={handleRemoveWorkOrder}
        title="Delete work order"
      />
      <NarrowSidebar
        closeButtonTestId="spec-close-sidebar"
        closeOnOutsideClick={false}
        height="100%"
        isOpen={sidebarOpen}
        onClose={onToggleSidebar}
        overlay={false}
        style={{ zIndex: 10 }}
        title="Work orders"
        width={expandedSidebarWidth}
      >
        <Box px="x1">
          <WorkOrderSearch
            onCategoryChange={onCategoryChange}
            onTextSearch={onTextSearch}
            searchCategory={searchCategory}
            searchCategoryOptions={searchCategoryOptions}
            searchText={searchText}
          />
          <Flex justifyContent="space-between">
            <EditFiltersButton
              className="spec-filters-applied"
              data-testid="filters-applied"
              icon="filter"
              onClick={handleClickEditFilters}
              size="small"
            >
              {filterLabel}
            </EditFiltersButton>
            {expandedFieldCount > 0 && (
              <Flex gap="2px" mr="half" zIndex={5}>
                <IconicButton icon="expand" onClick={onExpandAll} p={0} tooltip="Expand all" />
                <IconicButton icon="collapse" onClick={onCollapseAll} p={0} tooltip="Collapse all" />
              </Flex>
            )}
          </Flex>
        </Box>
        <VirtualizedWorkOrderList
          expandedWorkOrders={expandedWorkOrders}
          ListRef={ListRef}
          layoutSettingData={layoutSettingData}
          onToggleExpandWorkOrder={onToggleExpandWorkOrder}
          workOrders={workOrders}
        />
        <ApolloLoadingAnimation fetching={fetching} />
      </NarrowSidebar>
      {!sidebarOpen && <SidebarExpander onToggleSidebar={onToggleSidebar} />}
    </>
  );
}

export default React.memo(SidebarView, ReactExt.checkPropsForReRender);
